import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Nav,
    Pagination,
    Row,
    Spinner,
    Table,
} from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";
import PageLayout from "../../../../layouts/PageLayout";
import AddPaymentModal from "../AddPaymentModal";
import PaymentApprovalButton from "../PaymentApprovalButton";

const PAGE_SIZE = 20;

const formatAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount)
        ? amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? "-"
        : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const getPaymentStatusVariant = (status) => {
    switch ((status || "").toLowerCase()) {
        case "approved":
            return "success";
        case "cancelled":
            return "danger";
        default:
            return "warning";
    }
};

export default function IssuerDetailPage() {
    const { id: issuerId } = useParams();
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [activeTab, setActiveTab] = useState("summary");
    const [issuerInfo, setIssuerInfo] = useState(null);
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [banks, setBanks] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: PAGE_SIZE,
        total: 0,
        from: 0,
        to: 0,
    });
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        const baseUrl = process.env.REACT_APP_IMG_URL || "";
        return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    const fetchIssuerDetails = useCallback(async (page = 1) => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/issuer/show/${issuerId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        Accept: "application/json",
                    },
                    params: { page, per_page: PAGE_SIZE },
                }
            );
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Failed to load issuer ledger.");
            }

            const data = response.data?.data || {};
            setIssuerInfo(data.issuer || null);
            setSummary(data.summary || null);
            setProducts(Array.isArray(data.products) ? data.products : []);
            setPayments(Array.isArray(data.payments) ? data.payments : []);
            if (data.pagination) {
                setPagination({
                    current_page: data.pagination.current_page || page,
                    last_page: data.pagination.last_page || 1,
                    per_page: data.pagination.per_page || PAGE_SIZE,
                    total: data.pagination.total || 0,
                    from: data.pagination.from || 0,
                    to: data.pagination.to || 0,
                });
            }
        } catch (err) {
            console.error("Error fetching issuer ledger:", err);
            setError(err.response?.data?.message || err.message || "Failed to fetch issuer ledger.");
            setIssuerInfo(null);
            setSummary(null);
            setProducts([]);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [issuerId]);

    const fetchBanks = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/banks`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            });
            if (response.data?.success) setBanks(response.data.data || response.data.banks || []);
        } catch (err) {
            console.error("Error fetching banks:", err);
            setError((current) => current || err.response?.data?.message || "Failed to fetch banks.");
        }
    }, []);

    useEffect(() => {
        fetchIssuerDetails(currentPage);
    }, [fetchIssuerDetails, currentPage]);

    useEffect(() => {
        fetchBanks();
    }, [fetchBanks]);

    const handlePaymentSuccess = (message) => {
        setSuccessMsg(message || "Payment added successfully.");
        fetchIssuerDetails(currentPage);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) setCurrentPage(page);
    };

    const visibleProducts = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) return products;
        return products.filter((product) =>
            [product.title, product.product_code]
                .some((value) => String(value || "").toLowerCase().includes(keyword))
        );
    }, [products, search]);

    const settlementType = (summary?.settlement_type || "").toLowerCase();
    const userPays = summary?.payment_direction === "receive" || settlementType === "receivable";
    const userReceives = summary?.payment_direction === "pay" || settlementType === "payable";
    const summaryLabels = userPays
        ? {
            total: "Total amount owed by Issuer",
            settled: "Amount already paid by Issuer",
            remaining: "Amount still owed by Issuer",
            extra: "Extra amount paid by Issuer",
        }
        : userReceives
            ? {
                total: "Total amount payable to Issuer",
                settled: "Amount already paid to Issuer",
                remaining: "Amount still payable to Issuer",
                extra: "Extra amount paid to Issuer",
            }
            : {
                total: "Total amount",
                settled: "Settled amount",
                remaining: "Remaining amount",
                extra: "Extra amount",
            };

    // Salary, fixed and per-product amounts are all paid from the ledger.
    // The admin adds the payment; it counts once approved.
    const canAddPayment = summary?.who_adds_payment === "admin";

    return (
        <PageLayout>
            <Row className="mb-4">
                <Col xl={12}>
                    <Card className="border-0 shadow-sm text-white" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" }}>
                        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-4">
                            <div>
                                <h3 className="fw-bold mb-1">{issuerInfo?.name || t("Issuer Profile")}</h3>
                                <div className="text-white-50">{issuerInfo?.email || t("Issuer ledger and payment details")}</div>
                                <div className="small text-white-50 mt-2">
                                    <Link to="/issuers" className="text-white-50 text-decoration-none">{t("Issuers")}</Link>
                                    {" / "}{t("Ledger Details")}
                                    {issuerInfo?.payment_type && ` · ${t(issuerInfo.payment_type.replaceAll("_", " "))}`}
                                </div>
                            </div>
                            {canAddPayment && (
                                <Button
                                    variant="success"
                                    className="d-flex align-items-center gap-2 px-4 py-2 fw-semibold"
                                    onClick={() => setShowPaymentModal(true)}
                                    disabled={loading}
                                >
                                    <span className="material-icons">add_card</span>
                                    {t("Add Payment")}
                                </Button>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
            {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg("")}>{successMsg}</Alert>}

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom-0 pt-3">
                    <Nav variant="tabs" activeKey={activeTab} onSelect={(tab) => tab && setActiveTab(tab)}>
                        <Nav.Item><Nav.Link eventKey="summary">{t("Summary")}</Nav.Link></Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="products">
                                {t("Handed Over Products")} ({pagination.total})
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item><Nav.Link eventKey="payments">{t("Payments")} ({payments.length})</Nav.Link></Nav.Item>
                    </Nav>
                </Card.Header>

                {loading ? (
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <div className="small text-muted mt-2">{t("Loading issuer ledger...")}</div>
                    </Card.Body>
                ) : (
                    <>
                        {activeTab === "summary" && (
                            <Card.Body className="pt-4">
                                {summary ? (
                                    <Row className="justify-content-center">
                                        <Col lg={8} xl={7}>
                                            <Card className="border shadow-sm">
                                                <Card.Body className="p-0">
                                                    <div className={`p-4 border-bottom ${userPays ? "bg-warning-subtle" : userReceives ? "bg-success-subtle" : "bg-light"}`}>
                                                        <div className="small text-muted mb-1">
                                                            {t("Settlement summary")}
                                                            {summary.settlement_type && ` · ${t(summary.settlement_type)}`}
                                                        </div>
                                                        <h4 className={`fw-bold mb-1 ${userPays ? "text-warning-emphasis" : userReceives ? "text-success" : "text-dark"}`}>
                                                            {userPays
                                                                ? t("Issuer owes the company")
                                                                : userReceives
                                                                    ? t("Company owes the Issuer")
                                                                    : t("Issuer account balance")}
                                                        </h4>
                                                        <div className="text-muted">
                                                            {summary.extra_amount > 0
                                                                ? t(userPays
                                                                    ? "Issuer has paid more than the amount owed to the company."
                                                                    : userReceives
                                                                        ? "The company has paid more than the amount owed to the Issuer."
                                                                        : "The account has an extra payment.")
                                                                : summary.remaining_amount > 0
                                                                    ? t(userPays
                                                                        ? "The Issuer still owes this amount to the company."
                                                                        : userReceives
                                                                            ? "The company still owes this amount to the Issuer."
                                                                            : "This amount is still unsettled.")
                                                                    : t("The account is fully settled.")}
                                                        </div>
                                                    </div>
                                                    <div className="px-4">
                                                        {[
                                                            { label: summaryLabels.total, value: summary.total_amount, emphasized: true },
                                                            { label: summaryLabels.settled, value: summary.settled_amount },
                                                            { label: summaryLabels.remaining, value: summary.remaining_amount, emphasized: true },
                                                            { label: summaryLabels.extra, value: summary.extra_amount },
                                                            { label: "Opening balance", value: summary.opening_balance },
                                                            { label: "Earned from products handed over to listers", value: summary.earned_amount },
                                                            { label: "Payment method", value: summary.payment_type ? t(summary.payment_type.replaceAll("_", " ")) : "-", text: true },
                                                            { label: "Payment value", value: summary.payment_value },
                                                            { label: "Products handed over", value: summary.approved_products, count: true },
                                                        ].map((item, index, items) => (
                                                            <div
                                                                key={item.label}
                                                                className={`d-flex justify-content-between align-items-center gap-3 py-3 ${index < items.length - 1 ? "border-bottom" : ""}`}
                                                            >
                                                                <span className={item.emphasized ? "fw-semibold text-dark" : "text-muted"}>
                                                                    {t(item.label)}
                                                                </span>
                                                                <span className={`text-end ${item.emphasized ? "fw-bold text-dark fs-5" : "fw-semibold text-dark"}`}>
                                                                    {item.count
                                                                        ? item.value ?? 0
                                                                        : item.text
                                                                            ? item.value
                                                                            : `PKR ${formatAmount(item.value)}`}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                ) : (
                                    <div className="text-center text-muted py-4">{t("No summary data available.")}</div>
                                )}
                            </Card.Body>
                        )}

                        {activeTab === "products" && (
                            <Card.Body>
                                <Row className="g-3 align-items-end mb-3">
                                    <Col md={6} lg={5}>
                                        <Form.Group>
                                            <Form.Label className="small fw-semibold text-muted">{t("Search this page")}</Form.Label>
                                            <Form.Control
                                                type="search"
                                                value={search}
                                                onChange={(event) => setSearch(event.target.value)}
                                                placeholder={t("Product title or code")}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col className="small text-muted">
                                        {t("Products the Issuer added and handed over to a lister. Each one counts toward their earnings.")}
                                    </Col>
                                </Row>
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t("Product")}</th>
                                                <th>{t("Product Code")}</th>
                                                <th>{t("Status")}</th>
                                                <th>{t("Selling Price")}</th>
                                                <th className="text-end">{t("Handed Over")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleProducts.length ? visibleProducts.map((product) => {
                                                const imageUrl = resolveImageUrl(product.card_img);
                                                return (
                                                    <tr key={product.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-3">
                                                                {imageUrl ? (
                                                                    <img
                                                                        src={imageUrl}
                                                                        alt={product.title || t("Product")}
                                                                        className="rounded border object-fit-cover flex-shrink-0"
                                                                        style={{ width: 48, height: 48, cursor: "pointer" }}
                                                                        onClick={() => setPreviewImage(imageUrl)}
                                                                        onError={(event) => { event.currentTarget.style.visibility = "hidden"; }}
                                                                    />
                                                                ) : (
                                                                    <div className="rounded border bg-light d-flex align-items-center justify-content-center text-muted small flex-shrink-0" style={{ width: 48, height: 48 }}>
                                                                        {t("No image")}
                                                                    </div>
                                                                )}
                                                                <span className="fw-semibold">{product.title || "-"}</span>
                                                            </div>
                                                        </td>
                                                        <td>{product.product_code || "-"}</td>
                                                        <td><Badge bg="success">{t(product.status || "Handed over to lister")}</Badge></td>
                                                        <td>USD {formatAmount(product.sell_price_dollar)}</td>
                                                        <td className="text-end">{formatDate(product.approved_at)}</td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr><td colSpan={5} className="text-center text-muted py-5">{t("No handed-over products found on this page.")}</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                                {pagination.last_page > 1 && (
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pt-3 mt-3 border-top">
                                        <small className="text-muted">
                                            {t("Showing")} {pagination.from || 0} {t("to")} {pagination.to || 0} {t("of")} {pagination.total}
                                        </small>
                                        <Pagination size="sm" className="mb-0">
                                            <Pagination.Prev
                                                disabled={pagination.current_page <= 1}
                                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                            />
                                            {[...Array(pagination.last_page)].map((_, index) => (
                                                <Pagination.Item
                                                    key={index + 1}
                                                    active={index + 1 === pagination.current_page}
                                                    onClick={() => handlePageChange(index + 1)}
                                                >
                                                    {index + 1}
                                                </Pagination.Item>
                                            ))}
                                            <Pagination.Next
                                                disabled={pagination.current_page >= pagination.last_page}
                                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                            </Card.Body>
                        )}

                        {activeTab === "payments" && (
                            <Card.Body>
                                <div className="mb-3">
                                    <h5 className="fw-bold mb-1">{t("Payment history")}</h5>
                                    <small className="text-muted">
                                        {t("Only approved payments count toward the settlement; pending or cancelled payments do not.")}
                                    </small>
                                </div>
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>{t("Payment Date")}</th>
                                                <th>{t("Status")}</th>
                                                <th>{t("Payment Type")}</th>
                                                <th>{t("Bank")}</th>
                                                <th>{t("Recorded By")}</th>
                                                <th>{t("Notes")}</th>
                                                <th className="text-end">{t("Amount")}</th>
                                                <th>{t("Action")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.length ? payments.map((payment) => (
                                                <tr key={payment.id}>
                                                    <td>{formatDate(payment.payment_date)}</td>
                                                    <td>
                                                        <Badge bg={getPaymentStatusVariant(payment.status)}>
                                                            {t(payment.status || "pending")}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-capitalize">{t(payment.payment_type || "-")}</td>
                                                    <td>{payment.bank?.name || "-"}</td>
                                                    <td>{payment.creator?.name || "-"}</td>
                                                    <td>{payment.notes || "-"}</td>
                                                    <td className="text-end fw-semibold">PKR {formatAmount(payment.amount)}</td>
                                                    <td>
                                                        <PaymentApprovalButton
                                                            payment={payment}
                                                            onApproved={() => fetchIssuerDetails(currentPage)}
                                                            t={t}
                                                        />
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={9} className="text-center text-muted py-5">{t("No payment history found.")}</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        )}
                    </>
                )}
            </Card>

            <AddPaymentModal
                show={showPaymentModal}
                onHide={() => setShowPaymentModal(false)}
                listerId={issuerId}
                listerInfo={issuerInfo}
                entityType="issuer"
                banks={banks}
                t={t}
                onSuccess={handlePaymentSuccess}
            />

            <Modal show={Boolean(previewImage)} onHide={() => setPreviewImage(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fs-6 text-muted">{t("Product Preview")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-3">
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt={t("Product preview")}
                            className="img-fluid rounded-3 shadow"
                            style={{ maxHeight: "70vh", objectFit: "contain" }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}
